<template>
  <div>
    <div class="f-flex f-justify-between">
      <BaseRadio
        ref="radio"
        name="shipping_method"
        :value="shippingMethod.shippingMethodResource.id"
        :id="$id('shipping-' + shippingMethod.shippingMethodResource.id)"
        @input="shippingMethodChanged"
        v-model="selected"
        @click="showBalikovnaWidget"
      >
        <span
          :aria-label="`${
            shippingMethod.shippingMethodResource.name
          }, cena: ${formattedPrice(price)}`"
        >
          {{ shippingMethod.shippingMethodResource.name }}
        </span>
        <ShippingPopover
          v-if="shippingDescription"
          :description="shippingDescription"
        ></ShippingPopover>
        <div v-show="freeOfCharge" class="f-text-sm">
          {{ formattedFreeOfCharge(freeOfCharge) }}
        </div>
      </BaseRadio>
      <div v-if="this.freeShipping">
        <div class="f-text-right f-text-xs f-line-through">
          {{ formattedPrice(price) }}
        </div>
        <div
          class="f-text-right"
          v-bind:class="{ 'f-text-red-600': this.freeShipping }"
        >
          {{ $t("prices.free").toUpperCase() }}
        </div>
      </div>
      <div v-if="!this.freeShipping">
        {{ formattedPrice(price) }}
      </div>
    </div>
    <ValidationProvider
      :rules="rulesObject"
      :skipIfEmpty="false"
      v-slot="{ errors }"
    >
      <div v-show="isSelected">
        <label>
          <select
            class="f-form-select f-border-gray-300 f-rounded f-w-full f-mt-2 f-py-1 f-text-gray-700"
            ref="select"
            :id="
              $id('shipping-select-' + shippingMethod.shippingMethodResource.id)
            "
            :name="
              shippingMethod.shippingMethodResource.id + '_balikovna_branch_id'
            "
            v-model="selectedOption"
            v-on:click="showBalikovnaWidget"
            style="pointer-events: none"
          >
            <option
              v-for="option in options"
              :value="option.value"
              :key="option.value"
            >
              {{ option.text }}
            </option>
          </select>
        </label>
        <div v-if="getFirstError(errors)" class="f-text-sm f-text-red-600">
          {{ getFirstError(errors) }}
        </div>
      </div>
    </ValidationProvider>

    <!-- Modal -->
    <div
      v-if="showWidget"
      class="f-fixed f-inset-0 f-bg-black f-bg-opacity-50 f-z-40 f-flex f-items-center f-justify-center f-p-4"
    >
      <div
        class="f-bg-white f-rounded-lg f-p-2 f-pt-8 f-my-4 f-w-full f-h-full f-z-50 f-relative"
      >
        <button
          @click="closeWidget"
          class="f-absolute f-top-2 f-right-2 f-text-gray-500 hover:f-text-gray-700"
        >
          ✕
        </button>
        <iframe
          ref="balikovnaIframe"
          title="Výběr místa pro vyzvednutí zásilky"
          :src="widgetUrl"
          allow="geolocation"
          class="f-w-full f-h-full f-border-0"
        ></iframe>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import mixins from "vue-typed-mixins";
import InputValidationMixin from "@/components/Form/Basic/Inputs/InputValidationMixin";
import { ValidationProvider } from "vee-validate";
import Vue, { PropType } from "vue";
import FormShippingItem from "@/module/FormShippingItem";
import FormResource from "@/module/Form/Resources/FormResource";
import BaseRadio from "@/components/Form/Basic/Inputs/BaseRadio.vue";
import money from "@/module/Filters/MoneyFilter";
import ShippingPopover from "@/components/Form/ShippingMethods/ShippingPopover.vue";
import OrderPrices from "@/module/Order/OrderPrices";
import NumberHelper from "@/module/NumberHelper";
import SelectedShippingMethod from "@/module/Shipping/SelectedShippingMethod";

interface BalikovnaPoint {
  id: string;
  name: string;
  street: string;
  city: string;
  zip: string;
}

interface ComponentData {
  selected: number | null;
  selectedPoint: BalikovnaPoint | null;
  selectedOption: number | null;
  options: { value: number | null; text: string }[];
  showWidget: boolean;
  freeShipping: boolean;
}

export default mixins(InputValidationMixin).extend({
  name: "BalikovnaPersonalCollection",

  components: {
    ShippingPopover,
    ValidationProvider,
    BaseRadio,
  },

  props: {
    shippingMethod: {
      type: Object as PropType<FormShippingItem>,
      required: true,
    },
    currentlySelectedRadio: {
      type: null as unknown as PropType<SelectedShippingMethod | null>,
      required: true,
    },
    currency: {
      type: String,
      required: true,
    },
    order: {
      type: Object as PropType<OrderPrices>,
      required: true,
    },
    formResource: {
      type: Object as PropType<FormResource>,
      required: true,
    },
    country: {
      required: true,
    },
    totalPriceIncVat: {
      required: true,
    },
  },

  data(): ComponentData {
    return {
      selected: null,
      selectedPoint: null,
      selectedOption: null,
      options: [
        {
          value: null,
          text: String(this.$t("shipping_method.personal_collection.prompt")),
        },
      ],
      showWidget: false,
      freeShipping: false,
    };
  },

  computed: {
    widgetUrl(): string {
      return `https://b2c.cpost.cz/locations/?type=BALIKOVNY&skipLocation=false`;
    },

    freeOfCharge(): number | null {
      if (this.shippingMethod.freeOfCharges.hasOwnProperty("noFreeShipping")) {
        return null;
      }

      const shipping_form_item = this.shippingMethod.formItems.find((item) => {
        return item.name === this.country;
      });

      if (shipping_form_item === undefined) {
        return null;
      }

      const price = this.shippingMethod.freeOfCharges[this.currency];
      const includingVat =
        this.shippingMethod.shippingMethodResource.including_vat;
      const currentVat = shipping_form_item.currentVat;

      if (this.formResource.show_prices_including_vat) {
        return NumberHelper.priceIncludingVat(price, currentVat, includingVat);
      }
      return NumberHelper.priceWithOutVat(price, currentVat, includingVat);
    },

    isSelected: function (): boolean {
      return (
        this.shippingMethod.shippingMethodResource.id ===
        this.currentlySelectedRadio?.shipping_method_id
      );
    },

    rulesObject(): object {
      return this.isSelected ? { required: true } : {};
    },

    price(): number {
      const item = this.shippingMethod.formItems.find((item) => {
        return item.name === this.country;
      });

      if (item === undefined) {
        return 0;
      }

      if (this.freeOfCharge !== null) {
        this.updateFreeShipping(this.isEligibleForFreeShipping());
      }

      if (this.formResource.show_prices_including_vat) {
        return item.currentPriceIncVat;
      }

      return item.currentPrice;
    },

    shippingDescription(): string | null {
      return this.shippingMethod.shippingMethodResource.description;
    },
  },

  methods: {
    shippingMethodChanged(): void {
      const point = this.selectedPoint;

      this.$emit("shippingMethodChanged", {
        shipping_method_id: this.shippingMethod.shippingMethodResource.id,
        shipping_metadata: {
          balikovna_point_id: point?.id,
          balikovna_point_name: point?.name,
          balikovna_point_street: point?.street,
          balikovna_point_city: point?.city,
          balikovna_point_zip: point?.zip,
        },
        shipping_form_item: this.shippingMethod.formItems.find((item) => {
          return item.name === this.country;
        }),
      } as SelectedShippingMethod);
    },

    showBalikovnaWidget(): void {
      this.showWidget = true;
    },

    closeWidget(): void {
      this.showWidget = false;
    },

    handleBalikovnaSelection(event: MessageEvent): void {
      if (event.data.message === "pickerResult") {
        this.selectedPoint = {
          id: event.data.point.id, // neni treba do api se posila zip
          name: event.data.point.name,
          street: event.data.point.street,
          city: event.data.point.city,
          zip: event.data.point.zip,
        };

        this.options = [
          {
            value: null,
            text: String(this.$t("shipping_method.personal_collection.prompt")),
          },
          {
            value: Number(event.data.point.id),
            text: `${event.data.point.name}, ${event.data.point.street}`,
          },
        ];
        this.selectedOption = Number(event.data.point.id);
        this.shippingMethodChanged();
        this.closeWidget();
      }
    },

    formattedFreeOfCharge(price: number): string {
      if (price) {
        return (
          String(this.$t("shipping_method.free_shipping_from")) +
          money(price, this.$root.$i18n)
        );
      }
      return "";
    },

    updateFreeShipping(value: boolean): void {
      this.freeShipping = value;
    },

    isEligibleForFreeShippingHelper(vat: boolean): number {
      if (vat) {
        return this.order.totalPriceWoShippingIncludingVat;
      }
      return this.order.totalPriceWoShipping;
    },

    isEligibleForFreeShipping(): boolean {
      if (this.freeOfCharge !== null) {
        if (
          this.isEligibleForFreeShippingHelper(
            this.formResource.show_prices_including_vat,
          ) >= this.freeOfCharge
        ) {
          return true;
        }
      }
      return false;
    },

    formattedPrice(price: number): string {
      return money(price, this.$root.$i18n);
    },

    triggerVisibility(): void {
      const item = this.shippingMethod.formItems.find((item) => {
        return item.name === this.country;
      });

      const visible = item !== undefined;
      this.$emit("shippingMethodVisibilityChanged", { visible: visible });
    },
  },
  watch: {
    selected(newVal: number | null): void {
      if (
        newVal === this.shippingMethod.shippingMethodResource.id &&
        !this.selectedPoint
      ) {
        this.showBalikovnaWidget();
      }
    },

    totalPriceIncVat(): void {
      if (this.selected) {
        this.$emit("isEligibleForFreeShipping", {
          shipping_method_id: this.shippingMethod.shippingMethodResource.id,
          is_eligible_for_free_shipping: this.isEligibleForFreeShipping(),
        });
      }
    },

    country(): void {
      this.triggerVisibility();
      this.$emit("shippingMethodChanged", null);
      this.selected = null;
      this.selectedPoint = null;
      this.selectedOption = null;
    },
  },

  created(): void {
    this.triggerVisibility();
  },

  mounted(): void {
    const selectComponent = this.$refs.select as HTMLElement;
    const radioComponent = this.$refs.radio as Vue;

    selectComponent?.addEventListener("focus", this.showBalikovnaWidget);
    radioComponent.$el.addEventListener("click", this.showBalikovnaWidget);

    window.addEventListener("message", this.handleBalikovnaSelection);
  },

  beforeDestroy(): void {
    window.removeEventListener("message", this.handleBalikovnaSelection);
  },
});
</script>
